import React, { useState, useEffect } from 'react';
import { Form, Button, Table, Select, DatePicker, Space, Card, Row, Col, Typography, Spin, Collapse, Tooltip } from 'antd';
import { SearchOutlined, InfoCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { API_BASE_URL } from '../constants/api';
import './Reports.css';

const { Option } = Select;
const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;

const AccountReport = () => {
  const [form] = Form.useForm();
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [loadingReport, setLoadingReport] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [factors, setFactors] = useState({});
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);
  const [openingBalance, setOpeningBalance] = useState(0);

  // Default dates: start of current month to today
  const defaultStartDate = dayjs().startOf('month');
  const defaultEndDate = dayjs();

  useEffect(() => {
    fetchAccounts();
    fetchCurrenciesAndFactors();
    // Set default dates
    form.setFieldsValue({
      dateRange: [defaultStartDate, defaultEndDate]
    });
  }, []);

  const fetchCurrenciesAndFactors = async () => {
    setLoadingCurrencies(true);
    try {
      const [currRes, factRes] = await Promise.all([
        axios.get(`${API_BASE_URL}General/GetSupportedCurrencies`),
        axios.get(`${API_BASE_URL}General/Factors`)
      ]);
      setCurrencies(currRes.data);
      setFactors(factRes.data);
    } catch (error) {
      console.error('Failed to fetch currencies/factors:', error);
    } finally {
      setLoadingCurrencies(false);
    }
  };

  const fetchAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const response = await axios.get(`${API_BASE_URL}Account/List`);
      if (response.data.code === 0) {
        setAccounts(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
    }
    setLoadingAccounts(false);
  };

  const handleSearch = async () => {
    try {
      const values = await form.validateFields();
      const { accountId, dateRange } = values;
      
      if (!accountId || !dateRange || dateRange.length !== 2) {
        return;
      }

      setSelectedAccount(accountId);
      setLoadingReport(true);

      const response = await axios.get(`${API_BASE_URL}Reports/ReportAccount`, {
        params: {
          accountId,
          startDate: dateRange[0].format('YYYY-MM-DD'),
          endDate: dateRange[1].format('YYYY-MM-DD')
        }
      });

      if (response.data.code === 0) {
        setReportData(response.data.data || []);
        setOpeningBalance(response.data.openingBalance || 0);
      }
    } catch (error) {
      console.error('Report fetch error:', error);
    } finally {
      setLoadingReport(false);
    }
  };

  const getSymbol = (currency) => {
    if (!currency) return '?';
    const curStr = currency.toString();
    const cur = currencies.find(c => c.x.toString() === curStr);
    return cur ? cur.sympol : '?';
  };

  const tableColumns = [
    {
      title: 'التاريخ',
      dataIndex: 'date',
      key: 'date',
      render: (date) => dayjs(date).format('YYYY/MM/DD')
    },
    // {
    //   title: 'العملة',
    //   dataIndex: 'Currency',
    //   key: 'currency',
    //   render: (cur) => getSymbol(cur) || '?'
    // },
    {
      title: 'مدين',
      dataIndex: 'debitValue',
      key: 'debitValue',
      render: (val, record) => {
        if (!val) return '0';
        const usdAmount = record.factor * val;
        const roundedUsd = usdAmount.toFixed(2);
        const rateDisplay = record.factor && record.factor !== 1 ? (1 / record.factor).toFixed(2) : '1';
        return (
          <span>
            <b>{getSymbol(record.currency)}</b> {Number(val).toLocaleString()} 
            {record.currency == 1 ? null : (
              <b>({roundedUsd}$
                <Tooltip title={`$≈${Number(rateDisplay).toLocaleString()}`}>
                  <InfoCircleOutlined style={{ marginLeft: '4px', marginRight: '4px', color: '#1890ff' }} />
                </Tooltip>
              )</b>
            )}
          </span>
        );
      }
    },
    {
      title: 'دائن',
      dataIndex: 'creditValue',
      key: 'creditValue',
      render: (val, record) => {
        if (!val) return '0';
        const usdAmount = record.factor * val;
        const roundedUsd = usdAmount.toFixed(2);
        const rateDisplay = record.factor && record.factor !== 1 ? (1 / record.factor).toFixed(2) : '1';
        return (
          <span>
            <b>{getSymbol(record.currency)}</b> {Number(val).toLocaleString()} 
            {record.currency == 1 ? null : (
              <b>({roundedUsd}$
                <Tooltip title={`$≈${Number(rateDisplay).toLocaleString()}`}>
                  <InfoCircleOutlined style={{ marginLeft: '4px', marginRight: '4px', color: '#1890ff' }} />
                </Tooltip>
              )</b>
            )}
          </span>
        );
      }
    },
    {
      title: 'رقم السند',
      dataIndex: 'voucherId',
      key: 'voucherId'
    },
    {
      title: 'رقم الفاتورة',
      dataIndex: 'invoiceId',
      key: 'invoiceId'
    }
  ];


  const summaryData = reportData.reduce((acc, item) => {
    acc.totalDebit += item.debitValue * item.factor || 0;
    acc.totalCredit += item.creditValue * item.factor || 0;
    acc.balance += (item.debitValue * item.factor || 0) - (item.creditValue * item.factor || 0);
    return acc;
  }, { totalDebit: 0, totalCredit: 0, balance: 0 });

  // Add opening balance to credit total
  const balanceWithOpening = summaryData.balance + openingBalance;

  // Currency groups and USD balances
  const currencyGroups = React.useMemo(() => {
    const groups = {};
    let finalUsdBalance = 0;
    reportData.forEach(item => {
      const curStr = item.currency?.toString();
      if (!curStr) return;
      if (!groups[curStr]) {
        groups[curStr] = { currency: curStr, items: [], usdBalance: 0 };
      }
      const usdDebit = (item.debitValue || 0) * item.factor;
      const usdCredit = (item.creditValue || 0) * item.factor;
      groups[curStr].items.push(item);
      groups[curStr].usdBalance += usdDebit - usdCredit;
      finalUsdBalance += usdDebit - usdCredit;
    });
    // Subtract opening balance from final USD balance (opening balance is credit)
    finalUsdBalance -= openingBalance;
    return { groups: Object.values(groups).filter(g => g.items.length > 0), finalUsdBalance };
  }, [reportData, factors, openingBalance]);

  return (
    <div className="report-container">
      <Card size="small" className="report-filter-card">
        <Form form={form} layout="inline" className="report-filter-form">
          <Form.Item name="accountId" label="الحساب" rules={[{ required: true, message: 'يرجى اختيار الحساب' }]} className="report-form-item">
            <Select
              placeholder="اختر الحساب"
              loading={loadingAccounts}
              showSearch
              allowClear
              size="small"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={accounts.map(acc => ({
                label: `${acc.name} (${acc.id})`,
                value: acc.id
              }))}
            />
          </Form.Item>
          <Form.Item name="dateRange" label="الفترة" rules={[{ required: true, type: 'array', min: 2, message: 'يرجى اختيار الفترة' }]} className="report-form-item">
            <RangePicker
              format="YYYY/MM/DD"
              defaultValue={[defaultStartDate, defaultEndDate]}
              size="small"
            />
          </Form.Item>
          <Form.Item className="report-form-item report-btn-item">
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={loadingReport}
              size="small"
            >
              تنفيذ
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {reportData.length > 0 && (
        <>
          <Card size="small" className="report-stats-card">
            <Row gutter={[8, 8]}>
              <Col xs={12} sm={12} md={6}>
                <div className="stat-card stat-card-blue">
                  <div className="stat-label">الرصيد السابق</div>
                  <div className="stat-value">{(openingBalance).toLocaleString()}</div>
                </div>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <div className="stat-card stat-card-red">
                  <div className="stat-label">إجمالي المدين</div>
                  <div className="stat-value">{summaryData.totalDebit.toLocaleString()}</div>
                </div>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <div className="stat-card stat-card-green">
                  <div className="stat-label">إجمالي الدائن</div>
                  <div className="stat-value">{(summaryData.totalCredit).toLocaleString()}</div>
                </div>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <div className="stat-card stat-card-orange">
                  <div className="stat-label">الرصيد</div>
                  <div className="stat-value">{(balanceWithOpening).toLocaleString()}</div>
                </div>
              </Col>
            </Row>
          </Card>

          <Card size="small" className="report-table-card">
            <Spin spinning={loadingReport}>
              <Table
                columns={tableColumns}
                dataSource={reportData}
                rowKey="date"
                pagination={{ pageSize: 20 }}
                scroll={{ x: 'max-content' }}
                size="small"
              />
            </Spin>
          </Card>

          {/* Currency sections */}
          <Collapse size="small" className="report-collapse">
            <Panel header="تفصيل العملات" key="currency-details">
              <Spin spinning={loadingCurrencies}>
                {currencyGroups.groups.map(group => (
                  <Card
                    key={group.currency}
                    title={`${getSymbol(group.currency)} (${group.currency})`}
                    className="report-currency-card"
                    size="small"
                  >
                    <Table
                      columns={tableColumns.map(col => ({ ...col, title: col.title.replace(/مدين|دائن/, m => m) }))}
                      dataSource={group.items}
                      rowKey="date"
                      pagination={false}
                      size="small"
                      scroll={{ x: 'max-content' }}
                    />
                    <Row justify="end">
                      <Title level={5}>الرصيد بالدولار الأمريكي: $ {group.usdBalance.toLocaleString()}</Title>
                    </Row>
                  </Card>
                ))}
                <Card className="report-total-balance-card" size="small">
                  <Title level={5}>الرصيد النهائي بالدولار الأمريكي: $ {currencyGroups.finalUsdBalance.toLocaleString()}</Title>
                </Card>
              </Spin>
            </Panel>
          </Collapse>
        </>
      )}
    </div>
  );
};

export default AccountReport;
